// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { clientLogger } from '@seedplanner/logger';
import { getIsReactHydrationError, labels } from '@seedplanner/shared-utils';
import { routes } from '@seedplanner/shared-web';
import TraceKit from 'tracekit';
import './init';
import { render } from './render';
import { sendErrorReport } from './sendErrorReport';

const currentLoadTime = Date.now();
const INITIAL_LOAD_TIME_KEY = 'initialLoadTime';

const start = () => {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const currentWindow = window as any;
  TraceKit.report.subscribe((stack, _isWindowError, error) => {
    const firstStack = stack.stack[0] as TraceKit.StackFrame | undefined;
    const logInfo = {
      title: 'client_uncaught_exception',
      error: error?.message ?? 'no message',
      [labels.STACK_MESSAGE]: stack.message,
      [labels.STACK_LINENUMBER]: firstStack === undefined ? undefined : firstStack.line,
      [labels.STACK_COLUMNNUMBER]: firstStack === undefined ? undefined : firstStack.column,
    };
    clientLogger.error({ ...logInfo });
    const message = stack?.message ?? 'no message';
    const isReactHydrationError = getIsReactHydrationError(message);
    sendErrorReport({
      body: {
        message,
        location: window.location.pathname,
        lineNumber: firstStack === undefined ? undefined : firstStack.line,
        columnNumber: firstStack === undefined ? undefined : firstStack.column,
        functionName: firstStack === undefined ? undefined : firstStack.func,
        url: firstStack === undefined ? undefined : firstStack.url,
        nonce: currentWindow.__NONCE__ ?? undefined,
        clientHtml: isReactHydrationError ? document.body.innerHTML : undefined,
        currentLoadTime,
        initialLoadTime: sessionStorage.getItem(INITIAL_LOAD_TIME_KEY) ?? undefined,
      },
      labels: logInfo,
      endpoint: routes.TRACEKIT,
    })
      .then(() => {
        clientLogger.info({ ...logInfo }, 'Error report sent');
      })
      .catch((reportError) => {
        clientLogger.error({ ...logInfo, error: reportError }, 'Error report failed to send');
      });
  });
  TraceKit.collectWindowErrors = true;

  currentWindow.addEventListener('unhandledrejection', (event: PromiseRejectionEvent | undefined) => {
    let error = new Error('Unknown');
    if (event && event.reason instanceof Error) {
      error = event.reason;
    }

    const logInfo = { title: 'client_unhandled_rejection', error: error.message };
    clientLogger.error({ ...logInfo }, 'unhandled rejection at start');
    if (event) {
      event.preventDefault();
    }
  });

  const maybeExistingInitialLoadTime = sessionStorage.getItem(INITIAL_LOAD_TIME_KEY);
  if (maybeExistingInitialLoadTime == null) {
    sessionStorage.setItem(INITIAL_LOAD_TIME_KEY, Date.now().toString());
  }

  render();
};

start();
